/**
 * `data-screen-label="Agent"`, tab `channels` — **Put callers through to a person** (plan-018 WAPP-C; design §30.12;
 * ADR 0061; owner answers Q7 "this should be enabled on the customer portal or disabled" and Q8 "most people use SIP").
 *
 * Drawn under "When it answers", beside the answering cards that hold "Forward to a phone", in the same card grammar
 * (`ch-card`, the `c-answer-*` fields). The prototype draws no handoff panel, so the existing answering cards are the
 * visual authority.
 *
 * ```
 * Put callers through to a person                                   [Off | On | Not available yet]   (switch)
 *   why it cannot be switched on now (greyed switch), when no line that takes calls can put a caller through
 *   how it works: rings the number → a short spoken summary to the person only → joined → else a message
 *   The person's number  [+30 …]            The person hears the summary in  [language ▾]
 *   Where it works: one row per line, numbers on your own SIP trunks first (state tag + a sentence)
 *   the ring is not billed; from the person's answer it is talk time at that line's outbound rate
 *   [Save] [Discard]   problems, refusals, "Saved"
 * ```
 *
 * THE SETTING LIVES ON THE VOICEAGENT, BESIDE THE VERSION (OD-6): a save applies from the next conversation, with no
 * draft and no publish, so the save line says exactly that. While no line can put a caller through (production today:
 * both platform switches off) the switch cannot be turned on, the reason is said, and the number and the language can
 * still be saved — the API accepts a change that keeps it off. A setting that is already on stays switchable off.
 *
 * A REFUSAL IS SAID IN THE PORTAL'S LANGUAGE from its code and detail (`HandoffRefusedError`), each line's reason for
 * refusing the number named with its trunk; `409 revision_stale` reloads the setting and says so. The panel is hidden
 * for an agent whose kind does not put callers through (`offered: false`).
 *
 * AMENDED 2026-10-14 (owner decision; ADR 0061 amendment): the dek adds one line — a caller is put through only during
 * the agent's opening hours; outside them (special dates included) a caller who asks for a person is offered a message.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { handoffSettingRefusalOf, isLanguageCode } from "@wetalk/domain";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "../../new/answering.css";
import "../../../billing/billing.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { languageNameOf } from "../../../../api/conversation-view.js";
import { useGateway } from "../../../../api/provider.js";
import {
  HANDOFF_QUERY_KEY,
  HandoffRefusedError,
  useVoiceAgentHandoff,
  type VoiceAgentHandoffView,
} from "../../../../api/slice/handoff.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { SwitchControl } from "../../../billing/switch-control.js";
import {
  closedReasonTextOf,
  destinationOf,
  handoffClosedReasonOf,
  languageReasonTextOf,
  lineNameOf,
  lineNoteOf,
  lineStateTagOf,
  linesSipTrunkFirst,
  pathRefusalTextOf,
  settingProblemTextOf,
  unavailableReasonTextOf,
} from "./handoff-copy.js";

export interface HandoffPanelProps {
  readonly voiceAgentId: string;
  /** The live version's languages: the person hears the summary in one of them, in its voice. */
  readonly languageCode: readonly string[];
}

/** What the person has typed or chosen, `null` until they change it (the stored value applies). */
interface Draft {
  readonly enabled: boolean;
  readonly destination: string;
  readonly languageCode: string;
}

function draftOf(setting: VoiceAgentHandoffView): Draft {
  return {
    enabled: setting.enabled,
    destination: setting.destinationE164 ?? "",
    languageCode: setting.languageCode ?? "",
  };
}

function sameDraft(left: Draft, right: Draft): boolean {
  return (
    left.enabled === right.enabled &&
    destinationOf(left.destination) === destinationOf(right.destination) &&
    left.languageCode === right.languageCode
  );
}

/** A refusal in words: one sentence, or a heading and one line per refusing line. */
interface RefusalText {
  readonly sentence: string;
  readonly line: readonly string[];
}

const EXAMPLE_NUMBER = "+30 21 0123 4567";

export function HandoffPanel({ voiceAgentId, languageCode }: HandoffPanelProps): ReactNode {
  usePortalTranslation("messagingMedia");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const setting = useVoiceAgentHandoff(voiceAgentId);
  const [edited, setEdited] = useState<Draft | null>(null);
  const [savedLine, setSavedLine] = useState<"saved" | "reloaded" | null>(null);
  const [refusal, setRefusal] = useState<RefusalText | null>(null);

  const save = useMutation({
    mutationFn: async (input: { readonly draft: Draft; readonly stored: VoiceAgentHandoffView }) => {
      const destination = destinationOf(input.draft.destination);
      return gateway.changeVoiceAgentHandoff(
        voiceAgentId,
        {
          enabled: input.draft.enabled,
          destinationE164: destination === "" ? null : destination,
          languageCode: input.draft.languageCode === "" ? null : input.draft.languageCode,
        },
        input.stored.revision,
      );
    },
    onSuccess: (next) => {
      queryClient.setQueryData(HANDOFF_QUERY_KEY.voiceAgentHandoff(voiceAgentId), next);
      setEdited(null);
      setRefusal(null);
      setSavedLine("saved");
    },
    onError: async (error, input) => {
      if (error instanceof ApiError && error.code === "revision_stale") {
        await queryClient.invalidateQueries({ queryKey: HANDOFF_QUERY_KEY.voiceAgentHandoff(voiceAgentId) });
        setEdited(null);
        setRefusal(null);
        setSavedLine("reloaded");
        return;
      }
      setRefusal(refusalTextOf(error, input.stored));
    },
  });

  if (setting.isError) {
    return (
      <>
        <h2 className="ch-section-title">{portalText("messagingMedia", "handoff.title")}</h2>
        <p className="c-note c-note-critical" role="alert">
          {portalText("messagingMedia", "handoff.read_failed")}{" "}
          {apiErrorText(setting.error, portalText("messagingMedia", "handoff.reload"))}
        </p>
      </>
    );
  }
  if (setting.data === undefined) {
    return <p className="c-note">{portalText("messagingMedia", "handoff.reading")}</p>;
  }
  const stored = setting.data;
  if (!stored.offered) return null;

  const base = draftOf(stored);
  const draft = edited ?? base;
  const changed = !sameDraft(draft, base);
  const lines = linesSipTrunkFirst(stored.availability);
  const closedReason = handoffClosedReasonOf(stored.availability);
  // Turning ON needs a line that can put a caller through; turning a stored "on" OFF never does.
  const switchLocked = !canEdit || (closedReason !== null && !stored.enabled && !draft.enabled);
  const destination = destinationOf(draft.destination);
  const shapeRefusal = handoffSettingRefusalOf({
    enabled: draft.enabled,
    destinationE164: destination === "" ? null : destination,
    languageCode: draft.languageCode !== "" && isLanguageCode(draft.languageCode) ? draft.languageCode : null,
  });
  const problem = changed && shapeRefusal !== null ? settingProblemTextOf(shapeRefusal) : null;
  // The stored language stays choosable even when the live version no longer speaks it, so the select shows the truth.
  const languageChoice = [...new Set([...languageCode, ...(base.languageCode === "" ? [] : [base.languageCode])])];

  const change = (next: Partial<Draft>): void => {
    setSavedLine(null);
    setRefusal(null);
    setEdited({ ...draft, ...next });
  };

  const stateTag = stored.enabled ? (
    closedReason === null ? (
      <Tag status="positive">{portalText("messagingMedia", "handoff.tag.on")}</Tag>
    ) : (
      <Tag tone="accent-2">{portalText("messagingMedia", "handoff.tag.on_waiting")}</Tag>
    )
  ) : closedReason === null ? (
    <Tag tone="neutral">{portalText("messagingMedia", "handoff.tag.off")}</Tag>
  ) : (
    <Tag tone="neutral">{portalText("messagingMedia", "handoff.tag.not_available_yet")}</Tag>
  );

  return (
    <>
      <h2 className="ch-section-title">{portalText("messagingMedia", "handoff.title")}</h2>
      <p className="ch-section-dek">
        {portalText("messagingMedia", "handoff.dek")} {portalText("messagingMedia", "handoff.hours")}
      </p>

      <div className="ch-card ch-handoff" data-handoff-state={closedReason ?? "open"}>
        <div className="ch-handoff-head">
          <div className="ch-handoff-head-text">
            <h3 className="ch-card-heading ch-card-heading-flush">{portalText("messagingMedia", "handoff.card_heading")}</h3>
            {stateTag}
          </div>
          <SwitchControl
            label={portalText("messagingMedia", "handoff.switch_label")}
            labelVisible={false}
            checked={draft.enabled}
            disabled={switchLocked}
            onChange={(enabled) => {
              change({ enabled });
            }}
          />
        </div>

        {closedReason === null ? null : (
          <p className="ch-handoff-closed" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>
              {closedReasonTextOf(closedReason)}
              {stored.enabled ? ` ${portalText("messagingMedia", "handoff.closed.stays_on")}` : ""}
            </span>
          </p>
        )}

        <p className="ch-card-label ch-handoff-label">{portalText("messagingMedia", "handoff.how.label")}</p>
        <ol className="ch-handoff-steps">
          <li>{portalText("messagingMedia", "handoff.how.ask")}</li>
          <li>{portalText("messagingMedia", "handoff.how.summary")}</li>
          <li>{portalText("messagingMedia", "handoff.how.joined")}</li>
          <li>{portalText("messagingMedia", "handoff.how.no_answer")}</li>
        </ol>

        <div className="ch-handoff-fields">
          <div className="field">
            <label htmlFor="handoff-destination">{portalText("messagingMedia", "handoff.field.destination")}</label>
            <input
              id="handoff-destination"
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder={EXAMPLE_NUMBER}
              value={draft.destination}
              disabled={!canEdit}
              aria-invalid={problem !== null && shapeRefusal !== "language_missing" ? true : undefined}
              onChange={(event) => {
                change({ destination: event.currentTarget.value });
              }}
            />
            <span className="c-answer-note">{portalText("messagingMedia", "handoff.field.destination_hint", { example: EXAMPLE_NUMBER })}</span>
          </div>
          <div className="field">
            <label htmlFor="handoff-language">{portalText("messagingMedia", "handoff.field.language")}</label>
            <span className="c-answer-select">
              <select
                id="handoff-language"
                className="input"
                value={draft.languageCode}
                disabled={!canEdit}
                aria-invalid={problem !== null && shapeRefusal === "language_missing" ? true : undefined}
                onChange={(event) => {
                  change({ languageCode: event.currentTarget.value });
                }}
              >
                <option value="">{portalText("messagingMedia", "handoff.field.language_choose")}</option>
                {languageChoice.map((code) => (
                  <option key={code} value={code}>
                    {languageNameOf(code)}
                  </option>
                ))}
              </select>
              <Icon glyph={GLYPH.caretDown} size="sm" />
            </span>
            <span className="c-answer-note">{portalText("messagingMedia", "handoff.field.language_hint")}</span>
          </div>
        </div>

        <p className="ch-card-label ch-handoff-label">{portalText("messagingMedia", "handoff.lines.label")}</p>
        {lines.length === 0 ? (
          <p className="ch-card-note">{portalText("messagingMedia", "handoff.lines.none")}</p>
        ) : (
          <ul className="ch-handoff-lines">
            {lines.map((row) => (
              <li className="ch-handoff-line" key={`${row.path}:${row.sipTrunkId ?? ""}`} data-handoff-line={row.path}>
                <span className="ch-handoff-line-name">
                  <span>{lineNameOf(row)}</span>
                  <Tag
                    {...(row.state === "available"
                      ? { status: "positive" as const }
                      : row.state === "priority_message"
                        ? { tone: "accent-2" as const }
                        : { tone: "neutral" as const })}
                  >
                    {lineStateTagOf(row.state)}
                  </Tag>
                </span>
                <span className="ch-handoff-line-note">{lineNoteOf(row)}</span>
              </li>
            ))}
          </ul>
        )}

        <p className="ch-card-foot">{portalText("messagingMedia", "handoff.billing")}</p>

        {canEdit ? null : <p className="ch-card-foot">{portalText("messagingMedia", "handoff.read_only")}</p>}

        {changed || savedLine !== null || refusal !== null ? (
          <div className="ch-card-actions ch-handoff-actions">
            {changed ? (
              <Button
                variant="primary"
                disabled={problem !== null || save.isPending}
                onClick={() => {
                  setRefusal(null);
                  save.mutate({ draft, stored });
                }}
              >
                {save.isPending ? portalText("messagingMedia", "handoff.saving") : portalText("messagingMedia", "handoff.save")}
              </Button>
            ) : null}
            {changed ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEdited(null);
                  setRefusal(null);
                }}
              >
                {portalText("messagingMedia", "handoff.discard")}
              </Button>
            ) : null}
            {problem === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {problem}
              </span>
            )}
            {refusal === null ? null : (
              <div className="c-note c-note-critical ch-handoff-refusal" role="alert">
                <span>{refusal.sentence}</span>
                {refusal.line.length === 0 ? null : (
                  <ul>
                    {refusal.line.map((one) => (
                      <li key={one}>{one}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {savedLine === null ? null : (
              <span className="ch-note" role="status">
                {savedLine === "saved"
                  ? portalText("messagingMedia", "handoff.saved")
                  : portalText("messagingMedia", "handoff.refused.revision_stale")}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

/** A refused save, in the portal's language: the code and its detail, never the API's English sentence alone. */
export function refusalTextOf(error: unknown, stored: VoiceAgentHandoffView): RefusalText {
  if (error instanceof HandoffRefusedError) {
    if (error.code === "handoff_destination_refused") {
      return {
        sentence: portalText("messagingMedia", "handoff.refused.destination"),
        line: error.pathRefusal.map((one) => pathRefusalTextOf(one, stored.availability)),
      };
    }
    const reason =
      error.code === "handoff_unavailable" ? unavailableReasonTextOf(error.reason) : languageReasonTextOf(error.reason);
    if (reason !== null) return { sentence: reason, line: [] };
  }
  if (error instanceof ApiError && error.code === "handoff_not_offered") {
    return { sentence: portalText("messagingMedia", "handoff.refused.not_offered"), line: [] };
  }
  return { sentence: apiErrorText(error, portalText("messagingMedia", "handoff.refused.other")), line: [] };
}
